import { Editor, Element as SlateElement, Range, Transforms } from "slate";
import {
  Alignment,
  CustomEditor,
  CustomElement,
  ElementType,
  Format,
} from "../types";

export const getAlignmentClass = (align?: Alignment): string => {
  switch (align) {
    case "left":
      return "text-left";
    case "center":
      return "text-center";
    case "right":
      return "text-right";
    case "justify":
      return "text-justify";
    default:
      return "";
  }
};

export const toggleMark = (
  editor: CustomEditor,
  format: Format,
  value?: any,
) => {
  if (format === "size") {
    Editor.addMark(editor, format, value);
  } else {
    const isActive = isMarkActive(editor, format, value);
    if (isActive) {
      Editor.removeMark(editor, format);
    } else {
      Editor.addMark(editor, format, true);
    }
  }
};

export const isMarkActive = (
  editor: CustomEditor,
  format: Format,
  value?: any,
) => {
  const marks = Editor.marks(editor);
  if (format === "size") {
    return marks ? marks[format] === value : false;
  }
  return marks ? marks[format] === true : false;
};

export const toggleBlock = (editor: CustomEditor, format: ElementType) => {
  const isActive = isBlockActive(editor, format);
  const isList = format === "bulleted-list" || format === "numbered-list";

  Transforms.unwrapNodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      ((n as CustomElement).type === "bulleted-list" ||
        (n as CustomElement).type === "numbered-list"),
    split: true,
  });

  const newProperties: Partial<CustomElement> = {
    type: isActive ? "paragraph" : isList ? "list-item" : format,
  };

  Transforms.setNodes<CustomElement>(editor, newProperties);

  if (!isActive && isList) {
    const block = { type: format, children: [] } as CustomElement;
    Transforms.wrapNodes(editor, block);
  }
};

export const isBlockActive = (
  editor: CustomEditor,
  format: ElementType,
): boolean => {
  const { selection } = editor;
  if (!selection) return false;

  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: (n) =>
        !Editor.isEditor(n) &&
        SlateElement.isElement(n) &&
        (n as CustomElement).type === format,
    }),
  );

  return !!match;
};

export const toggleAlignment = (editor: CustomEditor, alignment: Alignment) => {
  const { selection } = editor;
  if (!selection) return;

  // Get all block nodes in the selection
  const blocks = Array.from(
    Editor.nodes(editor, {
      at: selection,
      match: (n) => SlateElement.isElement(n) && Editor.isBlock(editor, n),
    }),
  );

  // Toggle alignment for each block
  blocks.forEach(([node, path]) => {
    if (SlateElement.isElement(node)) {
      const currentAlign = node.align;
      const newAlign = currentAlign === alignment ? undefined : alignment;

      Transforms.setNodes(editor, { align: newAlign }, { at: path });
    }
  });
};
